// Stock Investments: one brokerage account split into buckets.
//
// A bucket owns a QUANTITY of a symbol and the cost of that quantity, so one
// ticker can sit in several buckets at once. Whatever no bucket claims is the
// default bucket's (Discretionary), which is never stored — so a bucket added
// later starts empty and every existing split stays exactly as it was.

import { n } from './sections';

const money = (v) => (v === null ? null : v.toFixed(2));

/** A position scaled to `quantity` of it, with `cost` as its cost basis. */
function slice(p, quantity, cost) {
    const held = n(p.quantity);
    const ratio = held ? quantity / held : 0;
    const value = n(p.marketValueBase) === null ? null : n(p.marketValueBase) * ratio;
    return {
        ...p,
        quantity: String(quantity),
        marketValue: value === null ? null : money(value),
        marketValueBase: value === null ? null : money(value),
        costBasis: money(cost),
        unrealizedPnl: value !== null && cost !== null ? money(value - cost) : null,
        unrealizedPnlBase: value !== null && cost !== null ? money(value - cost) : null,
        // A share of the whole account's NAV no longer describes a slice.
        percentOfNav: null,
    };
}

/** Allocations grouped by symbol. */
export const bySymbol = (allocations = []) => allocations.reduce((map, a) => {
    if (!map.has(a.symbol)) map.set(a.symbol, []);
    map.get(a.symbol).push(a);
    return map;
}, new Map());

/**
 * The positions in one bucket.
 *
 * An allocation's own cost is used where entered; otherwise its share of the
 * position's cost, pro rata. The default bucket gets the remainder of both —
 * its cost is what is left once the others' are taken out, unless one of them
 * is unknown, when it falls back to its pro-rata share too.
 */
export function bucketPositions(positions = [], allocations = [], bucket, defaultBucket) {
    const grouped = bySymbol(allocations);
    return positions.flatMap((p) => {
        const held = n(p.quantity) ?? 0;
        const cost = n(p.costBasis);
        const mine = grouped.get(p.symbol) ?? [];
        const proRata = (q) => (cost === null || held === 0 ? null : (cost * q) / held);

        if (bucket !== defaultBucket) {
            const a = mine.find((x) => x.bucket === bucket);
            if (!a || held <= 0) return [];
            // Shares sold since the split leave the buckets claiming more than
            // is held. Each gives up its share of the shortfall, so the buckets
            // never add up to more than the position.
            const claimed = mine.reduce((s, x) => s + x.quantity, 0);
            const scale = claimed > held ? held / claimed : 1;
            const quantity = a.quantity * scale;
            const ownCost = a.costBasis === null || a.costBasis === undefined ? null : a.costBasis * scale;
            return [slice(p, quantity, ownCost ?? proRata(quantity))];
        }

        const claimed = mine.reduce((s, a) => s + a.quantity, 0);
        // Cash and anything never split stay whole.
        if (mine.length === 0) return [p];
        const quantity = Math.max(0, held - claimed);
        if (quantity <= 0) return [];
        const claimedCost = mine.every((a) => a.costBasis !== null) && cost !== null
            ? cost - mine.reduce((s, a) => s + a.costBasis, 0)
            : null;
        return [slice(p, quantity, claimedCost ?? proRata(quantity))];
    });
}

/**
 * A bucket's return over the chart's window: TODAY's shares of each symbol,
 * held throughout, priced at each day's close. A bucket is a split of today's
 * holdings and has no history of its own, so its trades in the window are not
 * in this line — the account's own line is the one that carries them.
 *
 * `symbols` is each symbol's price return (0% at the window's start). Cash,
 * and a symbol with no prices, is held flat at its value.
 */
export function bucketReturns(rows = [], symbols = {}) {
    const holdings = rows
        .map((p) => ({ value: n(p.marketValueBase) ?? 0, points: p.isCash ? null : symbols[p.symbol] ?? null }))
        .filter((h) => h.value !== 0);
    const dates = [...new Set(holdings.flatMap((h) => (h.points ?? []).map((p) => p.date)))].sort();
    if (dates.length < 2 || !holdings.some((h) => h.points)) return [];
    const at = (points, date) => {
        let found = null;
        for (const p of points) { if (p.date > date) break; found = p; }
        return found;
    };
    // Each holding's value on a date, from today's value and its price path.
    const valueOn = (h, date) => {
        if (!h.points) return h.value;
        const now = 1 + h.points.at(-1).pct / 100;
        const then = at(h.points, date);
        return then ? (h.value * (1 + then.pct / 100)) / now : h.value / now;
    };
    const totals = dates.map((date) => [date, holdings.reduce((s, h) => s + valueOn(h, date), 0)]);
    const base = totals[0][1];
    if (!(base > 0)) return [];
    return totals.map(([date, v]) => ({ date, pct: (v / base - 1) * 100 }));
}

/** Starting (cost), current and P/L of a bucket's positions — All only, since
 *  a bucket has no history of its own. Cash counts at its value. */
export function bucketPart(positions, bucket, label) {
    const current = positions.reduce((s, p) => s + (n(p.marketValueBase) ?? 0), 0);
    const costs = positions.map((p) => (p.isCash ? n(p.marketValueBase) : n(p.costBasis)));
    const start = costs.some((c) => c === null) ? null : costs.reduce((s, c) => s + c, 0);
    return {
        key: `bucket:${bucket}`,
        section: 'stocks',
        label,
        windows: { all: { start, current, fromDate: null } },
    };
}
